import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  AlertCircle,
  CheckCircle,
  Phone,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { countries } from "../../data/countries";
import OtpVerificationModal from "../../components/OtpVerificationModal";
import apiService from "../../services/api";

const JobApplicationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const getPositionFromPath = () => {
    if (location.pathname.includes("driver")) return "Driver";
    if (location.pathname.includes("physiotherapist")) return "Physiotherapist";
    return "Healthcare Assistant";
  };
  const position = getPositionFromPath();
  const isDriver = position === "Driver";
  const isPhysiotherapist = position === "Physiotherapist";
  const isHealthcareAssistant = position === "Healthcare Assistant";

  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Careers", link: "/careers" },
    { label: `${position} Application`, link: null },
  ];
  const [formData, setFormData] = useState({
    companyId: process.env.REACT_APP_COMPANY_ID || "allawah",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    currentCountry: "",
    nationality: "",
    rightToWork: "",
    workPermitStatus: "",
    visaAcknowledged: false,
    positionAppliedFor: position,
    employmentType: [],
    availability: [],
    startDate: "",
    qualification: "",
    fieldOfStudy: "",
    hasCareTraining: "",
    careTrainingDetails: "",
    qualificationCountry: "",
    willingToTrain: "",
    yearsExperience: "",
    careSettings: [],
    careExperienceDescription: "",
    drivingLicence: "",
    licenceClass: "",
    yearsDriverExperience: "",
    physiotherapyRegistration: "",
    registrationNumber: "",
    ahpraRegistered: "",
    yearsPhysioExperience: "",
    specializations: [],
    skills: [],
    whyAllawah: "",
    whyAustralia: "",
    policeClearance: "",
    workingWithChildrenCheck: "",
    cvFile: null,
    consentConfirmed: false,
  });

  const [showWorkPermitFields, setShowWorkPermitFields] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      positionAppliedFor: position,
    }));
  }, [position]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "rightToWork") {
      setShowWorkPermitFields(value === "no" || value === "unsure");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMultiSelect = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter((item) => item !== value)
        : [...prev[name], value],
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!formData.email) {
      setSubmitError("Please enter your email address");
      return;
    }

    if (!isEmailVerified) {
      setIsSubmitting(true);
      try {
        await apiService.requestOtp(formData.email);
        setShowOtpModal(true);
      } catch (error) {
        if (
          error.message.includes("duplicate") ||
          error.message.includes("already submitted")
        ) {
          setSubmitError(
            "You have already submitted an application for this position. Duplicate applications are not allowed. Please check your email for confirmation of your previous application.",
          );
        } else {
          setSubmitError(
            error.message ||
              "Failed to send verification code. Please try again.",
          );
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const applicationData = {
        ...formData,
        cvFile: formData.cvFile?.name || null,
      };

      await apiService.submitApplication(formData.email, applicationData);
      setSubmitSuccess(true);

      setTimeout(() => {
        navigate("/careers");
      }, 3000);
    } catch (error) {
      if (
        error.message.includes("duplicate") ||
        error.message.includes("already submitted")
      ) {
        setSubmitError(
          "You have already submitted an application for this position. Duplicate applications are not allowed. Please check your email for confirmation of your previous application.",
        );
      } else {
        setSubmitError(
          error.message || "Failed to submit application. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpVerified = async (code) => {
    try {
      await apiService.verifyOtp(formData.email, code);
      setIsEmailVerified(true);
      setShowOtpModal(false);
      setTimeout(() => {
        document.getElementById("application-form").requestSubmit();
      }, 500);
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="py-12 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: "#F5F0F1" }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span>|</span>}
                {crumb.link ? (
                  <Link
                    to={crumb.link}
                    className="hover:text-[#E85D2C] transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="max-w-4xl">
            <Link
              to="/careers"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#E85D2C] transition-colors mb-6"
            >
              <ArrowLeft size={16} />
              Back to Careers
            </Link>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4">
              {position} Application Form
            </h1>

            <p className="text-lg text-gray-700 font-light leading-relaxed mb-6">
              Join Allawah Lodge in Coolamon, NSW
            </p>

            <a
              href="tel:+61269272706"
              className="inline-flex items-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-6 py-3 rounded-full transition-colors"
            >
              <Phone size={20} />
              CALL: (02) 6927 2706
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="mx-4 md:mx-24 bg-white py-8 md:py-16 px-8 md:px-16 rounded-3xl mt-4 md:mt-8 mb-4 md:mb-8">
        <div className="max-w-4xl mx-auto">
          {submitSuccess && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle
                size={24}
                className="text-green-600 flex-shrink-0 mt-0.5"
              />
              <div>
                <h3 className="text-lg font-bold text-green-900 mb-2">
                  Application Submitted Successfully!
                </h3>
                <p className="text-sm text-green-800">
                  Thank you for applying to Allawah Lodge. We have received your
                  application and will review it shortly. You will receive a
                  confirmation email at {formData.email}.
                </p>
                <p className="text-sm text-green-800 mt-2">
                  Redirecting you back to careers page...
                </p>
              </div>
            </div>
          )}

          {submitError && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle
                size={24}
                className="text-red-600 flex-shrink-0 mt-0.5"
              />
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-2">
                  Submission Error
                </h3>
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            </div>
          )}

          <form
            id="application-form"
            onSubmit={handleSubmit}
            className="space-y-10"
          >
            <input type="hidden" name="companyId" value={formData.companyId} />

            {/* Section 1: Personal Information */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  1. Personal Information
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Please provide your basic contact details
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    country={"au"}
                    value={formData.phone}
                    onChange={(phone) =>
                      setFormData((prev) => ({ ...prev, phone }))
                    }
                    inputProps={{
                      name: "phone",
                      required: true,
                    }}
                    enableSearch={true}
                    searchPlaceholder="Search countries..."
                    preferredCountries={["au", "nz", "gb", "us", "in"]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Country of Residence{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="currentCountry"
                    value={formData.currentCountry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nationality <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Right to Work */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  2. Right to Work in Australia
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Employment eligibility information
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Right to Work in Australia{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="rightToWork"
                  value={formData.rightToWork}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                >
                  <option value="">Select an option</option>
                  <option value="yes">
                    I currently have the right to work in Australia
                  </option>
                  <option value="no">
                    I do not currently have the right to work in Australia
                  </option>
                  <option value="unsure">I am unsure</option>
                </select>
              </div>

              {showWorkPermitFields && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visa / Work Permit Status{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="workPermitStatus"
                    value={formData.workPermitStatus}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    <option value="require-sponsorship">
                      I require employer sponsorship
                    </option>
                    <option value="in-process">
                      I am in the process of securing permission
                    </option>
                    <option value="other">
                      Other (please specify in motivation section)
                    </option>
                  </select>
                </div>
              )}

              <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                <input
                  type="checkbox"
                  name="visaAcknowledged"
                  checked={formData.visaAcknowledged}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                />
                <label className="text-sm text-gray-700 font-light leading-relaxed">
                  I understand that employment is subject to having valid work
                  rights in Australia and meeting visa requirements.
                </label>
              </div>
            </div>

            {/* Section 3: Role & Availability */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  3. Role & Availability
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Position details and work preferences
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position Applied For
                </label>
                <input
                  type="text"
                  name="positionAppliedFor"
                  value={formData.positionAppliedFor}
                  onChange={handleInputChange}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Employment Type Sought <span className="text-red-500">*</span>
                </label>
                <div className="grid md:grid-cols-3 gap-3">
                  {["Full-time", "Part-time", "Casual"].map((type) => (
                    <div
                      key={type}
                      className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg"
                    >
                      <input
                        type="checkbox"
                        checked={formData.employmentType.includes(type)}
                        onChange={() =>
                          handleMultiSelect("employmentType", type)
                        }
                        className="w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                      />
                      <label className="text-sm text-gray-700">{type}</label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Availability <span className="text-red-500">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-3">
                  {["Day shifts", "Night shifts", "Weekends", "Flexible"].map(
                    (shift) => (
                      <div
                        key={shift}
                        className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg"
                      >
                        <input
                          type="checkbox"
                          checked={formData.availability.includes(shift)}
                          onChange={() =>
                            handleMultiSelect("availability", shift)
                          }
                          className="w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                        />
                        <label className="text-sm text-gray-700">{shift}</label>
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Earliest Available Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                />
              </div>
            </div>

            {/* Section 4: Qualifications */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  4. Qualifications & Training
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Educational background and certifications
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Highest Academic Qualification{" "}
                  <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select your highest level of education completed.
                </p>
                <select
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                >
                  <option value="">Select your qualification level</option>
                  <option value="phd">PhD / Doctorate</option>
                  <option value="masters">Master's Degree</option>
                  <option value="postgrad-diploma">Postgraduate Diploma</option>
                  <option value="bachelors">Bachelor's Degree</option>
                  <option value="diploma">Diploma</option>
                  <option value="certificate">Certificate III/IV</option>
                  <option value="secondary">Year 12 / HSC</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field of Study / Program{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fieldOfStudy"
                    value={formData.fieldOfStudy}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Nursing, Physiotherapy, General Studies"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country Where Qualification Was Obtained{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="qualificationCountry"
                    value={formData.qualificationCountry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Healthcare Assistant Specific Fields */}
              {isHealthcareAssistant && (
                <>
                  <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Do you have any healthcare or care-related
                        certifications?
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        This could include First Aid, Certificate III in
                        Individual Support, Aged Care courses, etc.
                      </p>
                      <div className="flex gap-6">
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="hasCareTraining"
                            value="yes"
                            checked={formData.hasCareTraining === "yes"}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">Yes</label>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="hasCareTraining"
                            value="no"
                            checked={formData.hasCareTraining === "no"}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">No</label>
                        </div>
                      </div>
                    </div>

                    {formData.hasCareTraining === "yes" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Please list your care-related certifications
                        </label>
                        <textarea
                          name="careTrainingDetails"
                          value={formData.careTrainingDetails}
                          onChange={handleInputChange}
                          rows="4"
                          placeholder="e.g., Certificate III in Individual Support (Ageing), First Aid Certificate"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Physiotherapist Specific Fields */}
              {isPhysiotherapist && (
                <>
                  <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Are you registered with AHPRA?{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Australian Health Practitioner Regulation Agency
                        registration is required to practice in Australia.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="ahpraRegistered"
                            value="yes"
                            checked={formData.ahpraRegistered === "yes"}
                            onChange={handleInputChange}
                            required
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">
                            Yes, I am currently registered
                          </label>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="ahpraRegistered"
                            value="in-progress"
                            checked={formData.ahpraRegistered === "in-progress"}
                            onChange={handleInputChange}
                            required
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">
                            Application in progress
                          </label>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="ahpraRegistered"
                            value="no"
                            checked={formData.ahpraRegistered === "no"}
                            onChange={handleInputChange}
                            required
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">
                            No, not yet registered
                          </label>
                        </div>
                      </div>
                    </div>

                    {formData.ahpraRegistered === "yes" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          AHPRA Registration Number
                        </label>
                        <input
                          type="text"
                          name="registrationNumber"
                          value={formData.registrationNumber}
                          onChange={handleInputChange}
                          placeholder="PHY0000000"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Driver Specific Fields */}
              {isDriver && (
                <>
                  <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Do you hold a valid Australian driver's licence?{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-6">
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="drivingLicence"
                            value="yes"
                            checked={formData.drivingLicence === "yes"}
                            onChange={handleInputChange}
                            required
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">Yes</label>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="drivingLicence"
                            value="no"
                            checked={formData.drivingLicence === "no"}
                            onChange={handleInputChange}
                            required
                            className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                          />
                          <label className="text-sm text-gray-700">No</label>
                        </div>
                      </div>
                    </div>

                    {formData.drivingLicence === "yes" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Licence Class{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="licenceClass"
                            value={formData.licenceClass}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                          >
                            <option value="">Select licence class</option>
                            <option value="C">Class C (Car)</option>
                            <option value="LR">LR (Light Rigid)</option>
                            <option value="MR">MR (Medium Rigid)</option>
                            <option value="HR">HR (Heavy Rigid)</option>
                            <option value="HC">HC (Heavy Combination)</option>
                            <option value="MC">MC (Multi Combination)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Years of Driving Experience
                          </label>
                          <input
                            type="text"
                            name="yearsDriverExperience"
                            value={formData.yearsDriverExperience}
                            onChange={handleInputChange}
                            placeholder="e.g., 5 years"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Are you willing to complete required training if needed?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="willingToTrain"
                      value="yes"
                      checked={formData.willingToTrain === "yes"}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                    />
                    <label className="text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="willingToTrain"
                      value="no"
                      checked={formData.willingToTrain === "no"}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-[#E85D2C] border-gray-300 focus:ring-[#E85D2C]"
                    />
                    <label className="text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Experience (Healthcare Assistant) */}
            {isHealthcareAssistant && (
              <div className="space-y-6">
                <div className="pb-4 border-b-2 border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">
                    5. Care Experience
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Your experience in care settings
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience in Care
                  </label>
                  <input
                    type="text"
                    name="yearsExperience"
                    value={formData.yearsExperience}
                    onChange={handleInputChange}
                    placeholder="e.g., 2 years"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Care Settings Worked In
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      "Residential aged care",
                      "Home care",
                      "Hospitals",
                      "Disability support",
                      "Other",
                    ].map((setting) => (
                      <div
                        key={setting}
                        className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg"
                      >
                        <input
                          type="checkbox"
                          checked={formData.careSettings.includes(setting)}
                          onChange={() =>
                            handleMultiSelect("careSettings", setting)
                          }
                          className="w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                        />
                        <label className="text-sm text-gray-700">
                          {setting}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brief Description of Care Experience
                  </label>
                  <textarea
                    name="careExperienceDescription"
                    value={formData.careExperienceDescription}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Describe your care experience..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select your areas of experience and skills (optional)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      "Personal care and hygiene",
                      "Dementia care",
                      "Mobility support",
                      "Palliative care",
                      "Companionship support",
                      "Medication assistance",
                    ].map((skill) => (
                      <div
                        key={skill}
                        className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg"
                      >
                        <input
                          type="checkbox"
                          checked={formData.skills.includes(skill)}
                          onChange={() => handleMultiSelect("skills", skill)}
                          className="w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                        />
                        <label className="text-sm text-gray-700">{skill}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 5: Experience (Physiotherapist) */}
            {isPhysiotherapist && (
              <div className="space-y-6">
                <div className="pb-4 border-b-2 border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">
                    5. Physiotherapy Experience
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Your professional experience and specializations
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Physiotherapy Experience
                  </label>
                  <input
                    type="text"
                    name="yearsPhysioExperience"
                    value={formData.yearsPhysioExperience}
                    onChange={handleInputChange}
                    placeholder="e.g., 3 years"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Areas of Specialization (optional)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      "Geriatric physiotherapy",
                      "Neurological rehabilitation",
                      "Musculoskeletal",
                      "Cardiorespiratory",
                      "Sports physiotherapy",
                      "Paediatric physiotherapy",
                    ].map((spec) => (
                      <div
                        key={spec}
                        className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg"
                      >
                        <input
                          type="checkbox"
                          checked={formData.specializations.includes(spec)}
                          onChange={() =>
                            handleMultiSelect("specializations", spec)
                          }
                          className="w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                        />
                        <label className="text-sm text-gray-700">{spec}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 6: Motivation */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isHealthcareAssistant || isPhysiotherapist ? "6" : "5"}.
                  Motivation
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Tell us why you want to join our team
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Why do you want to work with Allawah Lodge?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="whyAllawah"
                  value={formData.whyAllawah}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  placeholder="Tell us what attracts you to our organization..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Why are you interested in working in Australia?
                </label>
                <textarea
                  name="whyAustralia"
                  value={formData.whyAustralia}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Share your reasons for wanting to work in Australia..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                />
              </div>
            </div>

            {/* Section 7: Background Checks */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isHealthcareAssistant || isPhysiotherapist ? "7" : "6"}.
                  Background Checks
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Required clearances for employment
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    National Police Check
                  </label>
                  <select
                    name="policeClearance"
                    value={formData.policeClearance}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    <option value="yes">I have a current police check</option>
                    <option value="can-obtain">
                      I can obtain one if required
                    </option>
                    <option value="no">I do not have one</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Working with Children Check
                  </label>
                  <select
                    name="workingWithChildrenCheck"
                    value={formData.workingWithChildrenCheck}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    <option value="yes">I have a current check</option>
                    <option value="can-obtain">
                      I can obtain one if required
                    </option>
                    <option value="no">I do not have one</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 8: Documents */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isHealthcareAssistant || isPhysiotherapist ? "8" : "7"}.
                  Upload Documents
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Attach your CV/Resume
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CV / Resume (PDF, DOC, DOCX - Max 5MB)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    name="cvFile"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="cv-upload"
                  />
                  <label
                    htmlFor="cv-upload"
                    className="flex items-center justify-center gap-3 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#E85D2C] transition-colors bg-gray-50"
                  >
                    <Upload size={24} className="text-gray-400" />
                    <div className="text-center">
                      {formData.cvFile ? (
                        <div>
                          <p className="text-sm font-medium text-[#E85D2C]">
                            {formData.cvFile.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(formData.cvFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Click to upload your CV
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            or drag and drop
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Section 9: Consent */}
            <div className="space-y-6">
              <div className="pb-4 border-b-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isHealthcareAssistant || isPhysiotherapist ? "9" : "8"}.
                  Declaration & Consent
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Please review and confirm
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="consentConfirmed"
                    checked={formData.consentConfirmed}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-4 h-4 text-[#E85D2C] border-gray-300 rounded focus:ring-[#E85D2C]"
                  />
                  <label className="text-sm text-gray-700 font-light leading-relaxed">
                    I confirm that all information provided in this application
                    is true and accurate to the best of my knowledge. I
                    understand that providing false or misleading information
                    may result in the rejection of my application or termination
                    of employment. I consent to Allawah Lodge collecting,
                    storing, and processing my personal information for
                    recruitment purposes in accordance with the Privacy Act 1988
                    (Cth).
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t-2 border-gray-200">
              <button
                type="submit"
                disabled={isSubmitting || submitSuccess}
                className="w-full bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium py-4 px-8 rounded-full transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEmailVerified
                      ? "Submitting Application..."
                      : "Sending Verification Code..."}
                  </>
                ) : submitSuccess ? (
                  <>
                    <CheckCircle size={20} />
                    Application Submitted!
                  </>
                ) : (
                  <>
                    {isEmailVerified
                      ? "Submit Application"
                      : "Verify Email & Submit"}
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center mt-4">
                {!isEmailVerified &&
                  "You will receive a verification code via email before submission"}
              </p>
            </div>
          </form>
        </div>
      </section>

      <OtpVerificationModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        email={formData.email}
        onVerified={handleOtpVerified}
      />
    </div>
  );
};

export default JobApplicationForm;
